
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { LanguageProvider } from "@/contexts/LanguageContext";
import SkipToContent from "./components/accessibility/SkipToContent";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Auth from "./pages/Auth";
import OAuthConsent from "./pages/OAuthConsent";
import AuthStatus from "./pages/AuthStatus";
import Portfolio from "./pages/Portfolio";
import WarumIch from "./pages/WarumIch";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Services from "./pages/Services";
import ProductVideos from "./pages/ProductVideos";
import SocialMediaAds from "./pages/SocialMediaAds";
import WhyFutureLevel from "./pages/WhyFutureLevel";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Privacy from "./pages/Privacy";
import Legal from "./pages/Legal";
import Terms from "./pages/Terms";
import Pricing from "./pages/Pricing";
import CookieBanner from "./components/CookieBanner";
import Comparison from "./pages/Comparison";
import EuropeanCityServicePage from "./pages/services/EuropeanCityServicePage";
import AutomatedAcquisitionEngine from "./pages/AutomatedAcquisitionEngine";
import ContentAuthorityDomination from "./pages/ContentAuthorityDomination";
import BundlesPage from "./pages/BundlesPage";
import FinanceIndustry from "./pages/industries/FinanceIndustry";
import LuxuryIndustry from "./pages/industries/LuxuryIndustry";
import TourismIndustry from "./pages/industries/TourismIndustry";
import HealthcareIndustry from "./pages/industries/HealthcareIndustry";
import EnhancedBlogPost from "./examples/EnhancedBlogPost";
import EnhancedStorytellingBlogPost from "./examples/EnhancedStorytellingBlogPost";
import EnhancedSocialMediaBlogPost from "./examples/EnhancedSocialMediaBlogPost";
import EnhancedEcommerceBlogPost from "./examples/EnhancedEcommerceBlogPost";
import AIVideoService from "./pages/services/AIVideo";
import WebdesignService from "./pages/services/Webdesign";
import SEOService from "./pages/services/SEO";
import GoogleBusinessProfileService from "./pages/services/GoogleBusinessProfile";
import ZurichServicePage from "./pages/services/ZurichServicePage";
import SwissCityServicePage from "./pages/services/SwissCityServicePage";
import FrenchSwissCityServicePage from "./pages/services/FrenchSwissCityServicePage";
import ItalianSwissCityServicePage from "./pages/services/ItalianSwissCityServicePage";
import EnglishSwissCityServicePage from "./pages/services/EnglishSwissCityServicePage";
import CalculatorPage from "./pages/CalculatorPage";
import SEOROICalculatorPage from "./pages/SEOROICalculatorPage";
import CaseStudiesPage from "./pages/CaseStudiesPage";
import TechStartZurichCase from "./pages/case-studies/TechStartZurichCase";
import MedPraxisBaselCase from "./pages/case-studies/MedPraxisBaselCase";
import EcommerceLuxuryGenevaCase from "./pages/case-studies/EcommerceLuxuryGenevaCase";
import VideoEditingService from "./pages/services/VideoEditingService";
import AIInfluencerService from "./pages/services/AIInfluencerService";
import PaidAdsService from "./pages/services/PaidAdsService";
import ThreeDModelingService from "./pages/services/ThreeDModelingService";
import MotionGraphicsService from "./pages/services/MotionGraphicsService";
import PodcastEditingService from "./pages/services/PodcastEditingService";
import { UXUIDesignService } from "./pages/services/UXUIDesignService";
import { ECommerceCustomizationService } from "./pages/services/ECommerceCustomizationService";
import { ARVRExperiencesService } from "./pages/services/ARVRExperiencesService";
import { AIMusicSoundBrandingService } from "./pages/services/AIMusicSoundBrandingService";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <BrowserRouter>
        <LanguageProvider>
          <SkipToContent />
          <Toaster />
          <Sonner />
          <CookieBanner />
          <AnimatePresence mode="wait">
            <main id="main-content" tabIndex={-1}>
              <Routes>
                {/* Default route - redirect to preferred language */}
                <Route path="/" element={<Navigate to="/de" replace />} />
                
                {/* Language-specific root pages */}
                <Route path="/:lang" element={<Index />} />
                
                {/* Blog routes */}
                <Route path="/:lang/blog" element={<Blog />} />
                <Route path="/:lang/blog/:slug" element={<BlogPost />} />
                
                {/* Enhanced blog post route for the specific articles */}
                <Route path="/de/blog/ki-video-kosten-nutzen-erklaert" element={<EnhancedBlogPost />} />
                <Route path="/en/blog/ki-video-kosten-nutzen-erklaert" element={<EnhancedBlogPost />} />
                <Route path="/fr/blog/ki-video-kosten-nutzen-erklaert" element={<EnhancedBlogPost />} />
                <Route path="/it/blog/ki-video-kosten-nutzen-erklaert" element={<EnhancedBlogPost />} />
                
                {/* Enhanced blog post route for the storytelling article */}
                <Route path="/de/blog/ki-storytelling-videos-veraendert" element={<EnhancedStorytellingBlogPost />} />
                <Route path="/en/blog/ki-storytelling-videos-veraendert" element={<EnhancedStorytellingBlogPost />} />
                <Route path="/fr/blog/ki-storytelling-videos-veraendert" element={<EnhancedStorytellingBlogPost />} />
                <Route path="/it/blog/ki-storytelling-videos-veraendert" element={<EnhancedStorytellingBlogPost />} />
                
                {/* Enhanced blog post route for the social media article */}
                <Route path="/de/blog/ki-social-media-video-content" element={<EnhancedSocialMediaBlogPost />} />
                <Route path="/en/blog/ki-social-media-video-content" element={<EnhancedSocialMediaBlogPost />} />
                <Route path="/fr/blog/ki-social-media-video-content" element={<EnhancedSocialMediaBlogPost />} />
                <Route path="/it/blog/ki-social-media-video-content" element={<EnhancedSocialMediaBlogPost />} />
                
                {/* Enhanced blog post route for the ecommerce article */}
                <Route path="/de/blog/videomarketing-ecommerce-ki-produktvideos" element={<EnhancedEcommerceBlogPost />} />
                <Route path="/en/blog/videomarketing-ecommerce-ki-produktvideos" element={<EnhancedEcommerceBlogPost />} />
                <Route path="/fr/blog/videomarketing-ecommerce-ki-produktvideos" element={<EnhancedEcommerceBlogPost />} />
                <Route path="/it/blog/videomarketing-ecommerce-ki-produktvideos" element={<EnhancedEcommerceBlogPost />} />
                
                {/* Generic blog route redirects to language-specific blog */}
                <Route path="/blog" element={<Navigate to="/de/blog" replace />} />
                
                {/* Service pages */}
                <Route path="/de/leistungen" element={<Services />} />
                <Route path="/en/services" element={<Services />} />
                <Route path="/fr/services" element={<Services />} />
                <Route path="/it/servizi" element={<Services />} />
                
                {/* Pricing pages */}
                <Route path="/de/preise" element={<Pricing />} />
                <Route path="/en/pricing" element={<Pricing />} />
                <Route path="/fr/tarifs" element={<Pricing />} />
                <Route path="/it/prezzi" element={<Pricing />} />
                
                {/* Generic routes redirect to language-specific ones */}
                <Route path="/services" element={<Navigate to="/en/services" replace />} />
                <Route path="/pricing" element={<Navigate to="/en/pricing" replace />} />
                <Route path="/services/product-videos" element={<Navigate to="/en/services/product-videos" replace />} />
                <Route path="/services/productvideos" element={<Navigate to="/en/services/product-videos" replace />} />
                <Route path="/services/social-media-ads" element={<Navigate to="/en/services/social-media-ads" replace />} />
                <Route path="/services/socialmediaads" element={<Navigate to="/en/services/social-media-ads" replace />} />
                
                {/* Specific service pages */}
                {/* Product Videos - all language paths */}
                <Route path="/en/services/product-videos" element={<ProductVideos />} />
                <Route path="/de/leistungen/produktvideos" element={<ProductVideos />} />
                <Route path="/fr/services/videos-de-produits" element={<ProductVideos />} />
                <Route path="/it/servizi/video-di-prodotti" element={<ProductVideos />} />
                
                {/* Social Media Ads - all language paths */}
                <Route path="/en/services/social-media-ads" element={<SocialMediaAds />} />
                <Route path="/de/leistungen/social-media-ads" element={<SocialMediaAds />} />
                <Route path="/fr/services/publicites-medias-sociaux" element={<SocialMediaAds />} />
                <Route path="/it/servizi/pubblicita-social-media" element={<SocialMediaAds />} />
                
                {/* New Service Hub Pages */}
                {/* AI-Video Service - all language paths */}
                <Route path="/de/ai-video" element={<AIVideoService />} />
                <Route path="/en/ai-video" element={<AIVideoService />} />
                <Route path="/fr/video-ia" element={<AIVideoService />} />
                <Route path="/it/video-ai" element={<AIVideoService />} />
                
                {/* AI-Video Service with city parameter */}
                <Route path="/de/ai-video/:city" element={<AIVideoService />} />
                <Route path="/en/ai-video/:city" element={<AIVideoService />} />
                <Route path="/fr/video-ia/:city" element={<AIVideoService />} />
                <Route path="/it/video-ai/:city" element={<AIVideoService />} />
                
                {/* Webdesign Service - all language paths */}
                <Route path="/de/webdesign" element={<WebdesignService />} />
                <Route path="/en/web-design" element={<WebdesignService />} />
                <Route path="/fr/conception-web" element={<WebdesignService />} />
                <Route path="/it/web-design" element={<WebdesignService />} />
                
                {/* Webdesign Service with city parameter */}
                <Route path="/de/webdesign/:city" element={<WebdesignService />} />
                <Route path="/en/web-design/:city" element={<WebdesignService />} />
                <Route path="/fr/conception-web/:city" element={<WebdesignService />} />
                <Route path="/it/web-design/:city" element={<WebdesignService />} />
                
                {/* SEO Service - all language paths */}
                <Route path="/de/seo" element={<SEOService />} />
                <Route path="/en/seo" element={<SEOService />} />
                <Route path="/fr/referencement" element={<SEOService />} />
                <Route path="/it/seo" element={<SEOService />} />
                
                {/* SEO Service with city parameter */}
                <Route path="/de/seo/:city" element={<SEOService />} />
                <Route path="/en/seo/:city" element={<SEOService />} />
                <Route path="/fr/referencement/:city" element={<SEOService />} />
                <Route path="/it/seo/:city" element={<SEOService />} />
                
                {/* Google Business Profile Service - all language paths */}
                <Route path="/de/google-business-profile" element={<GoogleBusinessProfileService />} />
                <Route path="/en/google-business-profile" element={<GoogleBusinessProfileService />} />
                <Route path="/fr/google-business-profile" element={<GoogleBusinessProfileService />} />
                <Route path="/it/google-business-profile" element={<GoogleBusinessProfileService />} />
                
                {/* Google Business Profile Service with city parameter */}
                <Route path="/de/google-business-profile/:city" element={<GoogleBusinessProfileService />} />
                <Route path="/en/google-business-profile/:city" element={<GoogleBusinessProfileService />} />
                <Route path="/fr/google-business-profile/:city" element={<GoogleBusinessProfileService />} />
                <Route path="/it/google-business-profile/:city" element={<GoogleBusinessProfileService />} />
                
                {/* Zürich-specific service pages */}
                <Route path="/de/ai-video/zuerich" element={<ZurichServicePage />} />
                <Route path="/de/webdesign/zuerich" element={<ZurichServicePage />} />
                <Route path="/de/seo/zuerich" element={<ZurichServicePage />} />
                <Route path="/de/google-business-profile/zuerich" element={<ZurichServicePage />} />
                
                {/* Basel-specific service pages */}
                <Route path="/de/ai-video/basel" element={<SwissCityServicePage />} />
                <Route path="/de/webdesign/basel" element={<SwissCityServicePage />} />
                <Route path="/de/seo/basel" element={<SwissCityServicePage />} />
                <Route path="/de/google-business-profile/basel" element={<SwissCityServicePage />} />
                
                {/* Bern-specific service pages */}
                <Route path="/de/ai-video/bern" element={<SwissCityServicePage />} />
                <Route path="/de/webdesign/bern" element={<SwissCityServicePage />} />
                <Route path="/de/seo/bern" element={<SwissCityServicePage />} />
                <Route path="/de/google-business-profile/bern" element={<SwissCityServicePage />} />
                {/* Wave 2 City Pages - German */}
                <Route path="/de/ai-video/winterthur" element={<SwissCityServicePage />} />
                <Route path="/de/webdesign/winterthur" element={<SwissCityServicePage />} />
                <Route path="/de/seo/winterthur" element={<SwissCityServicePage />} />
                <Route path="/de/google-business-profile/winterthur" element={<SwissCityServicePage />} />
                
                <Route path="/de/ai-video/luzern" element={<SwissCityServicePage />} />
                <Route path="/de/webdesign/luzern" element={<SwissCityServicePage />} />
                <Route path="/de/seo/luzern" element={<SwissCityServicePage />} />
                <Route path="/de/google-business-profile/luzern" element={<SwissCityServicePage />} />
                
                <Route path="/de/ai-video/st-gallen" element={<SwissCityServicePage />} />
                <Route path="/de/webdesign/st-gallen" element={<SwissCityServicePage />} />
                <Route path="/de/seo/st-gallen" element={<SwissCityServicePage />} />
                <Route path="/de/google-business-profile/st-gallen" element={<SwissCityServicePage />} />
                
                {/* French City Pages - FR-CH */}
                <Route path="/fr/video-ia/geneve" element={<FrenchSwissCityServicePage />} />
                <Route path="/fr/webdesign/geneve" element={<FrenchSwissCityServicePage />} />
                <Route path="/fr/referencement/geneve" element={<FrenchSwissCityServicePage />} />
                <Route path="/fr/google-business-profile/geneve" element={<FrenchSwissCityServicePage />} />
                
                <Route path="/fr/video-ia/lausanne" element={<FrenchSwissCityServicePage />} />
                <Route path="/fr/webdesign/lausanne" element={<FrenchSwissCityServicePage />} />
                <Route path="/fr/referencement/lausanne" element={<FrenchSwissCityServicePage />} />
                <Route path="/fr/google-business-profile/lausanne" element={<FrenchSwissCityServicePage />} />
                
                {/* Calculators and Case Studies */}
                <Route path="/de/website-kosten-rechner" element={<CalculatorPage />} />
                <Route path="/de/seo-roi-rechner" element={<SEOROICalculatorPage />} />
                <Route path="/en/website-cost-calculator" element={<CalculatorPage />} />
                <Route path="/fr/calculateur-cout-site-web" element={<CalculatorPage />} />
                <Route path="/it/calcolatore-costo-sito-web" element={<CalculatorPage />} />
                
                {/* Case Studies */}
                <Route path="/:lang/case-studies" element={<CaseStudiesPage />} />
                <Route path="/:lang/erfolgsgeschichten" element={<CaseStudiesPage />} />
                <Route path="/:lang/etudes-de-cas" element={<CaseStudiesPage />} />
                <Route path="/:lang/studi-di-caso" element={<CaseStudiesPage />} />
                
                {/* Individual Case Studies */}
                <Route path="/:lang/case-study/techstart-zuerich" element={<TechStartZurichCase />} />
                <Route path="/:lang/case-study/medpraxis-basel" element={<MedPraxisBaselCase />} />
                <Route path="/:lang/case-study/ecommerce-geneve" element={<EcommerceLuxuryGenevaCase />} />
                <Route path="/:lang/erfolgsgeschichte/techstart-zuerich" element={<TechStartZurichCase />} />
                <Route path="/:lang/erfolgsgeschichte/medpraxis-basel" element={<MedPraxisBaselCase />} />
                <Route path="/:lang/etude-de-cas/ecommerce-geneve" element={<EcommerceLuxuryGenevaCase />} />
                
                {/* Italian Market (IT-CH) - Ticino */}
                <Route path="/it/webdesign/lugano" element={<ItalianSwissCityServicePage />} />
                <Route path="/it/seo/lugano" element={<ItalianSwissCityServicePage />} />
                <Route path="/it/video-ai/lugano" element={<ItalianSwissCityServicePage />} />
                <Route path="/it/google-business-profile/lugano" element={<ItalianSwissCityServicePage />} />
                
                {/* English Market (EN-CH) - International Business */}
                <Route path="/en/seo/zurich" element={<EnglishSwissCityServicePage />} />
                <Route path="/en/web-design/zurich" element={<EnglishSwissCityServicePage />} />
                <Route path="/en/ai-video/zurich" element={<EnglishSwissCityServicePage />} />
                <Route path="/en/google-business-profile/zurich" element={<EnglishSwissCityServicePage />} />
                <Route path="/en/seo/geneva" element={<EnglishSwissCityServicePage />} />
                <Route path="/en/web-design/geneva" element={<EnglishSwissCityServicePage />} />
                <Route path="/en/ai-video/geneva" element={<EnglishSwissCityServicePage />} />
                <Route path="/en/google-business-profile/geneva" element={<EnglishSwissCityServicePage />} />
                
                {/* Phase 7 Creative Media Services */}
                <Route path="/:lang/video-editing" element={<VideoEditingService />} />
                <Route path="/:lang/video-editing/:city" element={<VideoEditingService />} />
                <Route path="/:lang/ai-influencer" element={<AIInfluencerService />} />
                <Route path="/:lang/ai-influencer/:city" element={<AIInfluencerService />} />
                <Route path="/:lang/paid-ads" element={<PaidAdsService />} />
                <Route path="/:lang/paid-ads/:city" element={<PaidAdsService />} />
                <Route path="/:lang/3d-modeling" element={<ThreeDModelingService />} />
                <Route path="/:lang/3d-modeling/:city" element={<ThreeDModelingService />} />
                <Route path="/:lang/motion-graphics" element={<MotionGraphicsService />} />
                <Route path="/:lang/motion-graphics/:city" element={<MotionGraphicsService />} />
                <Route path="/:lang/podcast-editing" element={<PodcastEditingService />} />
                <Route path="/:lang/podcast-editing/:city" element={<PodcastEditingService />} />
                
                {/* Industry-specific pages - all language paths */}
                {/* Finance industry */}
                <Route path="/en/services/ai-video-marketing-finance" element={<FinanceIndustry />} />
                <Route path="/de/leistungen/ki-videomarketing-finanzsektor" element={<FinanceIndustry />} />
                <Route path="/fr/services/video-marketing-ia-finance" element={<FinanceIndustry />} />
                <Route path="/it/servizi/marketing-video-ia-finanza" element={<FinanceIndustry />} />
                
                {/* Finance industry with city parameter */}
                <Route path="/en/services/ai-video-marketing-finance/:city" element={<FinanceIndustry />} />
                <Route path="/de/leistungen/ki-videomarketing-finanzsektor/:city" element={<FinanceIndustry />} />
                <Route path="/fr/services/video-marketing-ia-finance/:city" element={<FinanceIndustry />} />
                <Route path="/it/servizi/marketing-video-ia-finanza/:city" element={<FinanceIndustry />} />
                
                {/* Luxury industry */}
                <Route path="/en/services/ai-video-marketing-luxury" element={<LuxuryIndustry />} />
                <Route path="/de/leistungen/ki-videomarketing-luxusmarken" element={<LuxuryIndustry />} />
                <Route path="/fr/services/video-marketing-ia-luxe" element={<LuxuryIndustry />} />
                <Route path="/it/servizi/marketing-video-ia-lusso" element={<LuxuryIndustry />} />
                
                {/* Luxury industry with city parameter */}
                <Route path="/en/services/ai-video-marketing-luxury/:city" element={<LuxuryIndustry />} />
                <Route path="/de/leistungen/ki-videomarketing-luxusmarken/:city" element={<LuxuryIndustry />} />
                <Route path="/fr/services/video-marketing-ia-luxe/:city" element={<LuxuryIndustry />} />
                <Route path="/it/servizi/marketing-video-ia-lusso/:city" element={<LuxuryIndustry />} />
                
                {/* Tourism industry */}
                <Route path="/en/services/ai-video-marketing-tourism" element={<TourismIndustry />} />
                <Route path="/de/leistungen/ki-videomarketing-tourismus" element={<TourismIndustry />} />
                <Route path="/fr/services/video-marketing-ia-tourisme" element={<TourismIndustry />} />
                <Route path="/it/servizi/marketing-video-ia-turismo" element={<TourismIndustry />} />
                
                {/* Tourism industry with city parameter */}
                <Route path="/en/services/ai-video-marketing-tourism/:city" element={<TourismIndustry />} />
                <Route path="/de/leistungen/ki-videomarketing-tourismus/:city" element={<TourismIndustry />} />
                <Route path="/fr/services/video-marketing-ia-tourisme/:city" element={<TourismIndustry />} />
                <Route path="/it/servizi/marketing-video-ia-turismo/:city" element={<TourismIndustry />} />
                
                {/* Healthcare industry */}
                <Route path="/en/services/ai-video-marketing-healthcare" element={<HealthcareIndustry />} />
                <Route path="/de/leistungen/ki-videomarketing-gesundheit" element={<HealthcareIndustry />} />
                <Route path="/fr/services/video-marketing-ia-sante" element={<HealthcareIndustry />} />
                <Route path="/it/servizi/marketing-video-ia-sanita" element={<HealthcareIndustry />} />
                
                {/* Healthcare industry with city parameter */}
                <Route path="/en/services/ai-video-marketing-healthcare/:city" element={<HealthcareIndustry />} />
                <Route path="/de/leistungen/ki-videomarketing-gesundheit/:city" element={<HealthcareIndustry />} />
                <Route path="/fr/services/video-marketing-ia-sante/:city" element={<HealthcareIndustry />} />
                <Route path="/it/servizi/marketing-video-ia-sanita/:city" element={<HealthcareIndustry />} />
                
                {/* Comparison pages - all language paths */}
                <Route path="/en/ai-video-marketing-vs-traditional-production" element={<Comparison />} />
                <Route path="/de/ki-videomarketing-vs-traditionelle-produktion" element={<Comparison />} />
                <Route path="/fr/video-marketing-ia-vs-production-traditionnelle" element={<Comparison />} />
                <Route path="/it/marketing-video-ia-vs-produzione-tradizionale" element={<Comparison />} />
                
                {/* Why Future Level page */}
                <Route path="/why-future-level" element={<WhyFutureLevel />} />
                <Route path="/de/warum-future-level" element={<WhyFutureLevel />} />
                <Route path="/en/why-future-level" element={<WhyFutureLevel />} />
                <Route path="/fr/pourquoi-future-level" element={<WhyFutureLevel />} />
                <Route path="/it/perche-future-level" element={<WhyFutureLevel />} />
                
                {/* Swiss city-specific landing pages */}
                <Route path="/de/zurich" element={<Index />} />
                <Route path="/en/zurich" element={<Index />} />
                <Route path="/fr/zurich" element={<Index />} />
                <Route path="/it/zurich" element={<Index />} />
                
                <Route path="/de/geneva" element={<Index />} />
                <Route path="/en/geneva" element={<Index />} />
                <Route path="/fr/geneve" element={<Index />} />
                <Route path="/it/ginevra" element={<Index />} />
                
                <Route path="/de/basel" element={<Index />} />
                <Route path="/en/basel" element={<Index />} />
                <Route path="/fr/bale" element={<Index />} />
                <Route path="/it/basilea" element={<Index />} />
                
                <Route path="/de/bern" element={<Index />} />
                <Route path="/en/bern" element={<Index />} />
                <Route path="/fr/berne" element={<Index />} />
                <Route path="/it/berna" element={<Index />} />
                
                <Route path="/de/lausanne" element={<Index />} />
                <Route path="/en/lausanne" element={<Index />} />
                <Route path="/fr/lausanne" element={<Index />} />
                <Route path="/it/losanna" element={<Index />} />

                {/* EU city-specific landing pages */}
                {/* Germany */}
                <Route path="/de/stuttgart" element={<EuropeanCityServicePage />} />
                <Route path="/en/stuttgart" element={<EuropeanCityServicePage />} />
                <Route path="/fr/stuttgart" element={<EuropeanCityServicePage />} />
                <Route path="/it/stuttgart" element={<EuropeanCityServicePage />} />
                
                <Route path="/de/muenchen" element={<EuropeanCityServicePage />} />
                <Route path="/en/munich" element={<EuropeanCityServicePage />} />
                <Route path="/fr/munich" element={<EuropeanCityServicePage />} />
                <Route path="/it/monaco" element={<EuropeanCityServicePage />} />
                
                {/* France */}
                <Route path="/de/lyon" element={<EuropeanCityServicePage />} />
                <Route path="/en/lyon" element={<EuropeanCityServicePage />} />
                <Route path="/fr/lyon" element={<EuropeanCityServicePage />} />
                <Route path="/it/lione" element={<EuropeanCityServicePage />} />
                
                <Route path="/de/paris" element={<EuropeanCityServicePage />} />
                <Route path="/en/paris" element={<EuropeanCityServicePage />} />
                <Route path="/fr/paris" element={<EuropeanCityServicePage />} />
                <Route path="/it/parigi" element={<EuropeanCityServicePage />} />
                
                {/* Italy */}
                <Route path="/de/mailand" element={<EuropeanCityServicePage />} />
                <Route path="/en/milan" element={<EuropeanCityServicePage />} />
                <Route path="/fr/milan" element={<EuropeanCityServicePage />} />
                <Route path="/it/milano" element={<EuropeanCityServicePage />} />
                
                <Route path="/de/como" element={<EuropeanCityServicePage />} />
                <Route path="/en/como" element={<EuropeanCityServicePage />} />
                <Route path="/fr/come" element={<EuropeanCityServicePage />} />
                <Route path="/it/como" element={<EuropeanCityServicePage />} />

                {/* Phase 11 & 12 Pages */}
                <Route path="/de/automated-acquisition" element={<AutomatedAcquisitionEngine />} />
                <Route path="/en/automated-acquisition" element={<AutomatedAcquisitionEngine />} />
                <Route path="/fr/acquisition-automatisee" element={<AutomatedAcquisitionEngine />} />
                <Route path="/it/acquisizione-automatizzata" element={<AutomatedAcquisitionEngine />} />
                
                <Route path="/de/content-authority" element={<ContentAuthorityDomination />} />
                <Route path="/en/content-authority" element={<ContentAuthorityDomination />} />
                <Route path="/fr/autorite-contenu" element={<ContentAuthorityDomination />} />
                <Route path="/it/autorita-contenuti" element={<ContentAuthorityDomination />} />
                
                {/* Bundles Pages */}
                <Route path="/de/bundles" element={<BundlesPage />} />
                <Route path="/en/bundles" element={<BundlesPage />} />
                <Route path="/fr/bundles" element={<BundlesPage />} />
                <Route path="/it/bundles" element={<BundlesPage />} />
                
                {/* About pages */}
                <Route path="/de/ueber-uns" element={<About />} />
                <Route path="/en/about" element={<About />} />
                <Route path="/fr/a-propos" element={<About />} />
                <Route path="/it/chi-siamo" element={<About />} />
                
                {/* Contact pages */}
                <Route path="/de/kontakt" element={<Contact />} />
                <Route path="/en/contact" element={<Contact />} />
                <Route path="/fr/contact" element={<Contact />} />
                <Route path="/it/contatto" element={<Contact />} />
                
                {/* Privacy pages */}
                <Route path="/de/datenschutz" element={<Privacy />} />
                <Route path="/en/privacy-policy" element={<Privacy />} />
                <Route path="/fr/politique-de-confidentialite" element={<Privacy />} />
                <Route path="/it/informativa-privacy" element={<Privacy />} />
                
                {/* Legal pages */}
                <Route path="/de/impressum" element={<Legal />} />
                <Route path="/en/legal" element={<Legal />} />
                <Route path="/fr/mentions-legales" element={<Legal />} />
                <Route path="/it/note-legali" element={<Legal />} />
                
                {/* Terms pages */}
                <Route path="/de/agb" element={<Terms />} />
                <Route path="/en/terms" element={<Terms />} />
                <Route path="/fr/conditions" element={<Terms />} />
                <Route path="/it/termini" element={<Terms />} />
                
                {/* Generic redirect */}
                <Route path="/agb" element={<Navigate to="/de/agb" replace />} />
                <Route path="/datenschutz" element={<Navigate to="/de/datenschutz" replace />} />
                <Route path="/impressum" element={<Navigate to="/de/impressum" replace />} />
                
                {/* Catch-all route with language parameter */}
                <Route path="/auth" element={<Auth />} />
                <Route path="/auth/status" element={<AuthStatus />} />

                {/* Private Seiten (noindex, nicht in der Sitemap) */}
                <Route path="/portfolio" element={<Portfolio />} />
                <Route path="/warum-ich" element={<WarumIch />} />
                <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />

                <Route path="/:lang/*" element={<NotFound />} />
                
                {/* Catch-all route */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>
          </AnimatePresence>
        </LanguageProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
